<script setup lang="ts">
import { Chart } from "chart.js";
import { v4 as uuidv4 } from "uuid";
import { Filters } from "~/core/models/filters";
import { barChartComputeYAxisTicks } from "~/core/utils/barChartComputeYAxisTicks";
import { useFetchDtwinsMetricsLifeCycleState } from "~/modules/dtwin/composables/useFetchDtwinsMetricsLifeCycleState";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";

const props = defineProps<{
  organizationCode?: number;
  selectedSectorsCode: number[];
  userAssignedSectorsCodes: string[];
}>();

const { t } = useI18n();
const chartId = `chart-${uuidv4()}`;
const chartRef = ref<HTMLCanvasElement>();
const chartInstance = ref<Chart | undefined>();

const { setFilter, isLoading, results, error, fetchData } =
  useFetchDtwinsMetricsLifeCycleState({
    organizationCode: props.organizationCode,
  });

const selectedSectorsComputed = computed(() => {
  if (props.selectedSectorsCode && props.selectedSectorsCode.length > 0) {
    return props.selectedSectorsCode.map((sectorCode) => sectorCode.toString());
  } else {
    return props.userAssignedSectorsCodes;
  }
});

async function loadData() {
  setFilter(Filters.SECTORS, selectedSectorsComputed.value);
  await fetchData();
}

const aggregatedData: ComputedRef<Record<string, number>> = computed(() => {
  if (results.value) {
    return Object.values(results.value).reduce(
      (acc, curr) => {
        Object.entries(curr).forEach(([key, value]) => {
          acc[key] = (acc[key] ?? 0) + (value ?? 0);
        });
        return acc;
      },
      {} as Record<string, number>,
    );
  }
  return {};
});

const translatedDataKeys: ComputedRef<string[]> = computed(() => {
  const translatedKeys: string[] = [];
  const dataKeys = Object.keys(aggregatedData.value);
  const existingDtwinLifeCycleStates = Object.values(
    DtwinLifeCycleState,
  ) as string[];

  dataKeys.forEach((key) =>
    existingDtwinLifeCycleStates.includes(key)
      ? translatedKeys.push(t(`dashboard.deviceStates.states.${key}`))
      : translatedKeys.push(key),
  );

  return translatedKeys;
});

const dataSetBackgroundColors = computed(() => {
  const backgroundColorArray: string[] = [];
  Object.keys(aggregatedData.value).forEach((key) => {
    switch (key) {
      case DtwinLifeCycleState.ACTIVATED:
        backgroundColorArray.push("#05603A");
        break;
      case DtwinLifeCycleState.DISABLED:
        backgroundColorArray.push("#D1D1D6");
        break;
      case DtwinLifeCycleState.REGISTERED:
        backgroundColorArray.push("#0E4981");
        break;
      case DtwinLifeCycleState.PREPROVISIONED:
        backgroundColorArray.push("#F18006");
        break;
    }
  });

  return backgroundColorArray;
});

const buildChart = () => {
  const chartCtx = chartRef.value?.getContext("2d");
  const yAxisConfiguration = barChartComputeYAxisTicks(aggregatedData.value);
  if (chartCtx) {
    chartInstance.value = new Chart(chartCtx, {
      type: "bar",
      data: {
        labels: translatedDataKeys.value,
        datasets: [
          {
            label: "",
            data: Object.values(aggregatedData.value),
            backgroundColor: dataSetBackgroundColors.value,
            barThickness: 32,
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            min: 0,
            max: yAxisConfiguration.max,
            ticks: {
              stepSize: yAxisConfiguration.stepSize,
            },
          },
        },
      },
    });
  }
};

watch(
  () => selectedSectorsComputed.value,
  async () => {
    await loadData();
    buildChart();
  },
);
onMounted(async () => {
  await loadData();
  buildChart();
});
</script>

<template>
  <dashboard-card
    :is-loading="isLoading"
    :error="error"
    :title="t('dashboard.deviceStates.title')"
  >
    <canvas :id="chartId" ref="chartRef" height="240" />
  </dashboard-card>
</template>
